import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LABEL_STATUS_IMPOR_MASTER, tekstRingkasImporMaster } from "@/lib/surveiKepnakes";

// "Tempel dari Excel" untuk master Kepnakes (PR #114): teks tempelan -> Periksa (server menilai, TIDAK menyimpan) ->
// pratinjau per baris -> "Simpan N baru" (server memeriksa ulang; hanya baris baru yg disimpan). Dipakai untuk Institusi
// (satu kolom) dan Program Studi (dua kolom: nama | jenjang), memakai gaya dialog "Tempel dari Excel" editor butir.
const WARNA = {
    baru: "text-emerald-700 dark:text-emerald-400",
    sudah_ada: "text-slate-500",
    duplikat_dalam_tempelan: "text-amber-700 dark:text-amber-400",
    tidak_valid: "text-rose-700 dark:text-rose-400",
};

export default function SurveiTempelMaster({ open, info, jenjang, institusi = [], institusiAwal = "", onTutup, onSelesai }) {
    const [teks, setTeks] = useState("");
    const [jenjangBawaan, setJenjangBawaan] = useState("");
    const [institusiBawaan, setInstitusiBawaan] = useState(""); // Program Studi: kosong = tempelan punya kolom Institusi
    const [hasil, setHasil] = useState(null);
    const [sibuk, setSibuk] = useState(false);
    const [galat, setGalat] = useState("");
    const [konfirmasi, setKonfirmasi] = useState(false);
    const [selesai, setSelesai] = useState(null);
    // Bawaan = institusi yg sedang dipilih di daftar, diterapkan SAAT dialog dibuka saja (jangan menimpa pilihan pengguna
    // bila daftar dimuat ulang di belakang layar).
    const awalTerbaru = useRef(institusiAwal);
    awalTerbaru.current = institusiAwal;
    useEffect(() => { if (open) setInstitusiBawaan(info.berjenjang ? awalTerbaru.current : ""); }, [open, info.berjenjang]);
    const url = `/survei/master-kepnakes/${info.jenis}`;
    const badan = () => ({
        teks,
        ...(info.berjenjang && jenjangBawaan ? { jenjang_bawaan: jenjangBawaan } : {}),
        ...(info.berjenjang && institusiBawaan ? { institusi_bawaan: institusiBawaan } : {}),
    });
    const baru = hasil ? hasil.ringkasan.baru : 0;

    const ubah = (aksi) => { aksi(); setHasil(null); setSelesai(null); setGalat(""); };
    const tutup = () => { setTeks(""); setJenjangBawaan(""); setInstitusiBawaan(""); setHasil(null); setGalat(""); setSelesai(null); setKonfirmasi(false); onTutup(); };

    const periksa = async () => {
        setSibuk(true); setGalat(""); setHasil(null); setSelesai(null);
        try { setHasil((await api.post(`${url}/impor-pratinjau`, badan())).data); }
        catch (e) { setGalat(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSibuk(false); }
    };
    const simpan = async () => {
        setKonfirmasi(false); setSibuk(true); setGalat("");
        try {
            const res = (await api.post(`${url}/impor`, badan())).data;
            setSelesai(res); setHasil(null); setTeks("");
            onSelesai(res);
        } catch (e) { setGalat(formatApiErrorDetail(e.response?.data?.detail)); }
        finally { setSibuk(false); }
    };

    const contoh = !info.berjenjang ? "Institusi\nContoh Poltekkes Jakarta"
        : (institusiBawaan ? "Program Studi\tJenjang\nContoh Keperawatan\tDiploma 3"
            : "Institusi\tProgram Studi\tJenjang\nContoh Poltekkes Jakarta\tKeperawatan\tDiploma 3");
    const kolomJenjang = info.berjenjang;

    return (
        <>
            <Dialog open={open} onOpenChange={(o) => !o && tutup()}>
                <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="master-tempel-dialog">
                    <DialogHeader>
                        <DialogTitle>Tempel dari Excel — {info.judul}</DialogTitle>
                        <DialogDescription>
                            {info.berjenjang
                                ? (institusiBawaan
                                    ? "Di Excel, blok kolom Program Studi dan Jenjang (boleh termasuk baris judul), salin, lalu tempel di sini. Semua baris masuk ke institusi yang dipilih di bawah; bila tanpa kolom Jenjang, pilih jenjang bawaan."
                                    : "Di Excel, blok tiga kolom: Institusi, Program Studi, dan Jenjang (boleh termasuk baris judul), salin, lalu tempel di sini. Institusi harus sudah ada di daftar Institusi (impor lebih dulu).")
                                : "Di Excel, blok satu kolom nama institusi (boleh termasuk baris judul), salin, lalu tempel di sini. Kolom lain (mis. daftar program studi) diabaikan."}
                        </DialogDescription>
                    </DialogHeader>
                    <Textarea value={teks} onChange={(e) => ubah(() => setTeks(e.target.value))} rows={8} className="font-mono text-xs"
                              placeholder={contoh} data-testid="master-tempel-teks" aria-label="Teks tempelan dari Excel" />
                    {info.berjenjang && (
                        <div className="max-w-sm space-y-1">
                            <label className="text-sm font-medium" id="master-tempel-institusi-label">Institusi bawaan (untuk tempelan tanpa kolom Institusi)</label>
                            <Select value={institusiBawaan || "__tanpa"} onValueChange={(v) => ubah(() => setInstitusiBawaan(v === "__tanpa" ? "" : v))}>
                                <SelectTrigger aria-labelledby="master-tempel-institusi-label" data-testid="master-tempel-institusi"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__tanpa">Tidak ada (tempelan punya kolom Institusi)</SelectItem>
                                    {institusi.map((i) => <SelectItem key={i.id} value={i.id}>{i.nama}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {info.berjenjang && (
                        <div className="max-w-xs space-y-1">
                            <label className="text-sm font-medium" id="master-tempel-jenjang-label">Jenjang bawaan (untuk baris tanpa jenjang)</label>
                            <Select value={jenjangBawaan || "__tanpa"} onValueChange={(v) => ubah(() => setJenjangBawaan(v === "__tanpa" ? "" : v))}>
                                <SelectTrigger aria-labelledby="master-tempel-jenjang-label" data-testid="master-tempel-jenjang"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="__tanpa">Tidak ada</SelectItem>
                                    {jenjang.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    {galat && <p role="alert" className="text-sm text-rose-700" data-testid="master-tempel-galat">{galat}</p>}
                    {selesai && (
                        <p role="status" className="rounded-lg border border-emerald-300 bg-emerald-50 p-2 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100" data-testid="master-tempel-selesai">
                            {tekstRingkasImporMaster(selesai)}
                        </p>
                    )}
                    {hasil && (
                        <div className="space-y-2" data-testid="master-tempel-hasil">
                            <p className="text-sm text-slate-700" data-testid="master-tempel-ringkasan">
                                {hasil.ringkasan.baru} baru · {hasil.ringkasan.sudah_ada} sudah ada · {hasil.ringkasan.duplikat_dalam_tempelan} duplikat dalam tempelan · {hasil.ringkasan.tidak_valid} tidak valid
                            </p>
                            {hasil.pemberitahuan.length > 0 && (
                                <ul className="space-y-1 rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:bg-amber-950 dark:text-amber-100" data-testid="master-tempel-pemberitahuan">
                                    {hasil.pemberitahuan.map((p, i) => <li key={i}>{p}</li>)}
                                </ul>
                            )}
                            <div className="max-h-64 overflow-auto rounded border border-slate-200">
                                <table className="compact w-full text-xs" data-testid="master-tempel-tabel">
                                    <thead className="sticky top-0 bg-slate-100">
                                        <tr><th>Baris</th>{kolomJenjang && <th>Institusi</th>}<th>Nama</th>{kolomJenjang && <th>Jenjang</th>}<th>Status</th></tr>
                                    </thead>
                                    <tbody>
                                        {hasil.baris.map((b) => (
                                            <tr key={b.baris} className="border-t border-slate-200 align-top" data-testid={`master-tempel-baris-${b.baris}`}>
                                                <td className="tabular-nums">{b.baris}</td>
                                                {kolomJenjang && <td className="whitespace-normal break-words">{b.institusi || "–"}</td>}
                                                <td className="whitespace-normal break-words">{b.nama || "–"}</td>
                                                {kolomJenjang && <td>{b.jenjang || "–"}</td>}
                                                <td className={`whitespace-normal ${WARNA[b.status] || ""}`}>
                                                    {LABEL_STATUS_IMPOR_MASTER[b.status] || b.status}{b.alasan && b.status !== "sudah_ada" ? ` — ${b.alasan}` : ""}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={tutup}>{selesai ? "Tutup" : "Batal"}</Button>
                        <Button variant="outline" onClick={periksa} disabled={!teks.trim() || sibuk} data-testid="master-tempel-periksa">Periksa</Button>
                        <Button onClick={() => setKonfirmasi(true)} disabled={!hasil || baru === 0 || sibuk}
                                className="bg-[#00B9AD] text-[#0F2E30] hover:bg-[#60C0D0]" data-testid="master-tempel-simpan">
                            Simpan {baru} baru
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <AlertDialog open={konfirmasi} onOpenChange={setKonfirmasi}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Simpan {baru} {info.judul.toLowerCase()} baru?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Hanya baris berstatus "Baru" yang disimpan dan langsung tampil di formulir survei Kepnakes. Yang sudah ada tidak diubah.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={simpan} data-testid="master-tempel-konfirmasi-ya">Ya, simpan</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
